/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 7/21/2025
 * for userx-support
 */

import {useState, useCallback, useRef, useMemo} from 'react';
import supportAPI from '../_apis/supportAPI';

/**
 * Normalizes a name to be a valid JavaScript property identifier
 * @param {string} name - The name to normalize
 * @returns {string} Normalized name (lowercase, special chars replaced with underscores)
 */
const normalizeName = (name) => {
    if (!name || typeof name !== 'string') return '';
    let normalized = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    // Prefix with underscore if it starts with a digit to make it a valid identifier
    if (normalized && /^[0-9]/.test(normalized)) {
        normalized = '_' + normalized;
    }
    return normalized;
};

const useDefaultsManager = (key) => {
    const [defaults, setDefaults] = useState([]);
    const [newDefault, setNewDefault] = useState({});
    const newDefaultRef = useRef(null);
    const [saved, setSaved] = useState(false);

    const fetchDefaults = useCallback(() => {
        supportAPI.get(`/options/${key}`).then(response => {
            if(!response.data) throw new Error('Defaults not loaded');
            const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
            const values = typeof data.option_value === 'string' ? JSON.parse(data.option_value) : data.option_value;
            setDefaults(values || []);
        }).catch(console.error);
    }, [key]);

    const addItem = useCallback((newItem) => {
        setDefaults(prev => [...prev, newItem]);
    }, []);

    const removeItem = useCallback((id) => {
        setDefaults(prev => prev.filter(i => i.id !== id));
    }, []);

    const saveDefaults = useCallback(() => {
        supportAPI.post(`/options`, {
            option_name: key,
            option_value: defaults
        }).then(response => {
            setSaved(true)
        })
        .catch(console.error);
    }, [key, defaults]);

    // Create an object with both the array (defaults) and name-based access
    const defaultsObject = useMemo(() => {
        const obj = {
            defaults,
            newDefault,
            setNewDefault,
            newDefaultRef,
            fetchDefaults,
            addItem,
            removeItem,
            saveDefaults,
            saved
        };

        // Add each default item as a property accessible by name
        const seenNames = new Set();
        const conflicts = [];
        
        defaults.forEach(item => {
            if (item.name) {
                const normalizedName = normalizeName(item.name);
                if (normalizedName) {
                    // Check for conflicts - if a name was already added, skip to avoid overwriting
                    if (!seenNames.has(normalizedName)) {
                        obj[normalizedName] = item;
                        seenNames.add(normalizedName);
                    } else {
                        conflicts.push({ name: item.name, normalized: normalizedName });
                    }
                }
            }
        });

        // Log conflicts outside of the main loop to make debugging easier
        if (conflicts.length > 0) {
            conflicts.forEach(({ name, normalized }) => {
                console.warn(
                    `useDefaultsManager: Duplicate normalized name "${normalized}" ` +
                    `for item "${name}". Use the defaults array to access this item.`
                );
            });
        }

        return obj;
    }, [defaults, newDefault, newDefaultRef, fetchDefaults, addItem, removeItem, saveDefaults, saved]);

    return defaultsObject;
};

export default useDefaultsManager;
